import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppLayout from '@/components/layout/AppLayout';
import Navbar from '@/components/ui/navbar';
import Typography from '@/components/ui/typography';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { RootState } from '@/store/store';
import { userService } from '@/services/userService';
import { MaiaFeatureFlag, MaiaFeatureFlagUser, TeamMember } from '@/types/api';
import { useToast } from '@/components/ui/toast';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Flag,
  MoreVertical,
  Plus,
  Rocket,
  Search,
  User,
  X,
} from 'lucide-react';

type AssignedUserRow = {
  userId: string;
  member: TeamMember | null;
};

const displayFlagTitle = (flag: MaiaFeatureFlag): string =>
  flag.name && flag.name.trim() ? flag.name : flag.key;

const displayTeamMemberName = (m: TeamMember): string => {
  const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  return name || m.email;
};

const displayAssignedRowName = (row: AssignedUserRow): string =>
  row.member ? displayTeamMemberName(row.member) : row.userId;

const FeatureIcon: React.FC<{ svg: string | null | undefined }> = ({ svg }) =>
  svg ? (
    <span
      className="text-[#5536F5] [&_svg]:h-5 [&_svg]:w-5"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  ) : (
    <Flag className="h-5 w-5 text-[#5536F5]" />
  );

const ConfigureFeatures: React.FC = () => {
  const { selectedHospital } = useSelector(
    (state: RootState) => state.hospitals,
  );
  const { addToast } = useToast();

  const [featureFlags, setFeatureFlags] = useState<MaiaFeatureFlag[] | null>(
    null,
  );
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [togglingFlagKey, setTogglingFlagKey] = useState<string | null>(null);

  useEffect(() => {
    const tenantId = selectedHospital?.tenantId;
    if (!tenantId) {
      setFeatureFlags(null);
      setFlagsLoading(false);
      return;
    }

    let cancelled = false;
    setFlagsLoading(true);

    userService
      .getFeatureFlags(tenantId)
      .then((data) => {
        if (!cancelled) setFeatureFlags(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setFeatureFlags(null);
          const message =
            err instanceof Error ? err.message : 'Failed to load feature flags';
          addToast({ message, type: 'error', duration: 5000 });
        }
      })
      .finally(() => {
        if (!cancelled) setFlagsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedHospital?.tenantId, addToast]);

  const visibleFlags = useMemo(
    () => (featureFlags ?? []).filter((f) => f.tenant_view),
    [featureFlags],
  );

  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editFlagKey, setEditFlagKey] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [assignedOverrides, setAssignedOverrides] = useState<
    MaiaFeatureFlagUser[]
  >([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [unassignInProgress, setUnassignInProgress] = useState(false);
  const [assignInProgress, setAssignInProgress] = useState(false);

  const editFlag = useMemo(
    () =>
      editFlagKey
        ? ((featureFlags ?? []).find((f) => f.key === editFlagKey) ?? null)
        : null,
    [editFlagKey, featureFlags],
  );

  useEffect(() => {
    if (!editSheetOpen || !editFlagKey) return;
    const tenantId = selectedHospital?.tenantId;
    if (!tenantId) {
      setAssignedOverrides([]);
      setTeamMembers([]);
      return;
    }

    let cancelled = false;
    setAssignedLoading(true);
    setTeamMembersLoading(true);
    setAssignedOverrides([]);
    setTeamMembers([]);

    const assignedPromise = userService
      .getUsersByFeatureFlag(tenantId, editFlagKey)
      .then((users) => {
        if (!cancelled) setAssignedOverrides(users);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setAssignedOverrides([]);
          const message =
            err instanceof Error
              ? err.message
              : 'Failed to load assigned users';
          addToast({ message, type: 'error', duration: 5000 });
        }
      });

    const teamPromise = userService
      .getAllTeamMembers(tenantId)
      .then((members) => {
        if (!cancelled) setTeamMembers(members);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTeamMembers([]);
          const message =
            err instanceof Error ? err.message : 'Failed to load team members';
          addToast({ message, type: 'error', duration: 5000 });
        }
      });

    Promise.all([assignedPromise, teamPromise]).finally(() => {
      if (!cancelled) {
        setAssignedLoading(false);
        setTeamMembersLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [editSheetOpen, editFlagKey, selectedHospital?.tenantId, addToast]);

  const teamMembersByUserId = useMemo(() => {
    const map = new Map<string, TeamMember>();
    teamMembers.forEach((m) => {
      map.set(m.uuid, m);
    });
    return map;
  }, [teamMembers]);

  const assignedRows = useMemo<AssignedUserRow[]>(
    () =>
      assignedOverrides.map((o) => ({
        userId: o.user_id,
        member: teamMembersByUserId.get(o.user_id) ?? null,
      })),
    [assignedOverrides, teamMembersByUserId],
  );

  const filteredAssignedRows = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return assignedRows;
    return assignedRows.filter((row) => {
      const label = displayAssignedRowName(row).toLowerCase();
      const email = row.member?.email.toLowerCase() ?? '';
      return label.includes(q) || email.includes(q);
    });
  }, [assignedRows, userSearch]);

  const assignedUserIdSet = useMemo(
    () => new Set(assignedOverrides.map((o) => o.user_id)),
    [assignedOverrides],
  );

  const availableTeamMembers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const assignable = teamMembers.filter(
      (m) => !assignedUserIdSet.has(m.uuid),
    );
    if (!q) return assignable;
    return assignable.filter((m) => {
      const label = displayTeamMemberName(m).toLowerCase();
      return label.includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [teamMembers, assignedUserIdSet, userSearch]);

  const handleFeatureFlagToggle = async (flagKey: string, checked: boolean) => {
    const tenantId = selectedHospital?.tenantId;
    if (!tenantId || featureFlags === null) return;

    setTogglingFlagKey(flagKey);
    try {
      await userService.updateFeatureFlag(tenantId, flagKey, checked);
      setFeatureFlags((prev) =>
        prev
          ? prev.map((f) =>
              f.key === flagKey ? { ...f, enabled: checked } : f,
            )
          : prev,
      );
      addToast({
        message: checked ? 'Feature enabled' : 'Feature disabled',
        type: 'success',
        duration: 4000,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to update feature';
      addToast({ message, type: 'error', duration: 5000 });
    } finally {
      setTogglingFlagKey(null);
    }
  };

  const openEditUsers = (flagKey: string) => {
    setEditFlagKey(flagKey);
    setUserSearch('');
    setEditSheetOpen(true);
  };

  const assignTeamMemberToFeature = async (m: TeamMember) => {
    const tenantId = selectedHospital?.tenantId;
    const userId = m.uuid;
    if (!tenantId || !editFlagKey || !userId) {
      addToast({
        message: 'Unable to add user. Try again.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setAssignInProgress(true);
    try {
      await userService.assignFeatureToUser(tenantId, {
        userId,
        featureKey: editFlagKey,
      });
      setAssignedOverrides((prev) =>
        prev.some((o) => o.user_id === userId)
          ? prev
          : [...prev, { user_id: userId, enabled_at: null }],
      );
      addToast({
        message: 'User added to this feature',
        type: 'success',
        duration: 4000,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add user';
      addToast({ message, type: 'error', duration: 5000 });
    } finally {
      setAssignInProgress(false);
    }
  };

  const unassignAssignedRow = async (row: AssignedUserRow) => {
    const tenantId = selectedHospital?.tenantId;
    if (!tenantId || !editFlagKey) {
      addToast({
        message: 'Unable to remove user. Try again.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setUnassignInProgress(true);
    try {
      await userService.unassignUserFromFeatureFlag(tenantId, {
        userId: row.userId,
        featureKey: editFlagKey,
      });
      setAssignedOverrides((prev) =>
        prev.filter((o) => o.user_id !== row.userId),
      );
      addToast({
        message: 'User removed from this feature',
        type: 'success',
        duration: 4000,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to remove user';
      addToast({ message, type: 'error', duration: 5000 });
    } finally {
      setUnassignInProgress(false);
    }
  };

  return (
    <AppLayout currentPage="Feature configuration">
      <Navbar
        title="Configure features"
        showSearch={true}
        showAvatar={true}
        showWelcomeMessage={false}
        icon={<Rocket className="h-5 w-5 text-neutral-700" />}
      />

      <div className="space-y-4 p-6">
        {flagsLoading && (
          <Typography variant="p" color="neutral500" size="14">
            Loading feature settings…
          </Typography>
        )}
        <div className="flex flex-col gap-4 max-w-full">
          {visibleFlags.map((flag) => {
            const title = displayFlagTitle(flag);
            const description = flag.description ?? '';
            const canEdit = flag.tenant_edit;
            const showOverrideMenu = canEdit && !flag.enabled;
            return (
              <div
                key={flag.key}
                className="flex items-center gap-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#5536F5]/10">
                  <FeatureIcon svg={flag.icon_svg} />
                </div>
                <div className="min-w-0 flex-1">
                  <Typography
                    variant="h3"
                    color="neutral1000"
                    weight="semibold"
                    size="16"
                    className="mb-1"
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant="p"
                    color="neutral500"
                    weight="normal"
                    size="14"
                  >
                    {description}
                  </Typography>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    checked={flag.enabled}
                    disabled={
                      !canEdit ||
                      flagsLoading ||
                      !selectedHospital?.tenantId ||
                      togglingFlagKey === flag.key
                    }
                    onCheckedChange={(checked) =>
                      handleFeatureFlagToggle(flag.key, checked)
                    }
                    aria-label={`Toggle ${title}`}
                  />
                  {showOverrideMenu && (
                    <button
                      type="button"
                      onClick={() => openEditUsers(flag.key)}
                      className="rounded-md p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 cursor-pointer"
                      aria-label={`Edit users for ${title}`}
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Sheet
        open={editSheetOpen}
        onOpenChange={(open) => {
          setEditSheetOpen(open);
          if (!open) {
            setEditFlagKey(null);
            setAssignedOverrides([]);
            setTeamMembers([]);
            setAssignedLoading(false);
            setTeamMembersLoading(false);
            setUnassignInProgress(false);
            setAssignInProgress(false);
          }
        }}
      >
        <SheetContent
          side="right"
          className="flex h-full w-full max-w-md flex-col gap-0 border-l border-neutral-200 bg-white p-0 sm:max-w-md"
        >
          <SheetHeader className="space-y-0 border-b border-neutral-100 p-6 text-left">
            <div className="flex gap-4 pr-8">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-100">
                <User className="h-5 w-5 text-neutral-600" />
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <SheetTitle className="text-lg font-semibold text-neutral-900">
                  Edit users
                </SheetTitle>
                <SheetDescription className="text-sm text-neutral-500">
                  {editFlag
                    ? `Add or remove users from ${displayFlagTitle(editFlag)}.`
                    : 'Add or remove users from this feature.'}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-4">
            <div className="shrink-0 border-b border-neutral-100 pb-4">
              <div className="relative w-full">
                <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <Input
                  placeholder="Search users"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="h-10 w-full rounded-xl border-neutral-200 pl-9"
                  aria-label="Search users"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain pt-4 pr-0.5">
              <section className="flex flex-col gap-2">
                <Typography
                  variant="p"
                  color="neutral700"
                  weight="semibold"
                  size="14"
                >
                  Added users
                </Typography>
                <ul className="space-y-2">
                  {editFlagKey &&
                    (assignedLoading ? (
                      <li className="rounded-xl border border-neutral-100 bg-neutral-50/50 px-3 py-4 text-center">
                        <Typography
                          variant="p"
                          color="neutral500"
                          weight="normal"
                          size="14"
                        >
                          Loading assigned users…
                        </Typography>
                      </li>
                    ) : filteredAssignedRows.length === 0 ? (
                      <li className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50/50 px-3 py-4 text-center">
                        <Typography
                          variant="p"
                          color="neutral500"
                          weight="normal"
                          size="14"
                        >
                          {userSearch.trim()
                            ? 'No assigned users match your search'
                            : 'No users assigned to this feature yet'}
                        </Typography>
                      </li>
                    ) : (
                      filteredAssignedRows.map((row) => {
                        const label = displayAssignedRowName(row);
                        return (
                          <li
                            key={row.userId}
                            className="flex items-center justify-between gap-3 rounded-xl border border-neutral-100 bg-white px-3 py-2.5"
                          >
                            <div className="min-w-0 flex-1">
                              <Typography
                                variant="p"
                                color="neutral1000"
                                weight="medium"
                                size="14"
                                className="truncate"
                              >
                                {label}
                              </Typography>
                              {row.member && label !== row.member.email && (
                                <Typography
                                  variant="p"
                                  color="neutral500"
                                  weight="normal"
                                  size="12"
                                  className="truncate"
                                >
                                  {row.member.email}
                                </Typography>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={unassignInProgress || assignInProgress}
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-red-500 bg-white text-red-600 shadow-sm transition-colors hover:bg-red-50 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => unassignAssignedRow(row)}
                              aria-label={`Remove ${label}`}
                            >
                              <X className="h-4 w-4 stroke-[2.5]" />
                            </button>
                          </li>
                        );
                      })
                    ))}
                </ul>
              </section>

              <section className="flex flex-col gap-2 border-t border-neutral-100 pt-4">
                <Typography
                  variant="p"
                  color="neutral700"
                  weight="semibold"
                  size="14"
                >
                  Add users
                </Typography>
                <ul className="space-y-2">
                  {editFlagKey &&
                    (teamMembersLoading ? (
                      <li className="rounded-xl border border-neutral-100 bg-neutral-50/50 px-3 py-4 text-center">
                        <Typography
                          variant="p"
                          color="neutral500"
                          weight="normal"
                          size="14"
                        >
                          Loading team members…
                        </Typography>
                      </li>
                    ) : availableTeamMembers.length === 0 ? (
                      <li className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50/50 px-3 py-4 text-center">
                        <Typography
                          variant="p"
                          color="neutral500"
                          weight="normal"
                          size="14"
                        >
                          {userSearch.trim()
                            ? 'No team members match your search'
                            : 'Everyone on the team is already assigned to this feature'}
                        </Typography>
                      </li>
                    ) : (
                      availableTeamMembers.map((m) => {
                        const label = displayTeamMemberName(m);
                        return (
                          <li
                            key={m.uuid}
                            className="flex items-center justify-between gap-3 rounded-xl border border-neutral-100 bg-white px-3 py-2.5"
                          >
                            <div className="min-w-0 flex-1">
                              <Typography
                                variant="p"
                                color="neutral1000"
                                weight="medium"
                                size="14"
                                className="truncate"
                              >
                                {label}
                              </Typography>
                              {label !== m.email && (
                                <Typography
                                  variant="p"
                                  color="neutral500"
                                  weight="normal"
                                  size="12"
                                  className="truncate"
                                >
                                  {m.email}
                                </Typography>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={assignInProgress || unassignInProgress}
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-green-600 bg-white text-green-600 shadow-sm transition-colors hover:bg-green-50 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => assignTeamMemberToFeature(m)}
                              aria-label={`Add ${label}`}
                            >
                              <Plus className="h-4 w-4 stroke-[2.5]" />
                            </button>
                          </li>
                        );
                      })
                    ))}
                </ul>
              </section>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
};

export default ConfigureFeatures;
